import { createContext, useContext, useEffect, useState } from "react";
import { api, BACKEND } from "./api";

const SiteCtx = createContext({});
export function SiteProvider({ children }) {
  const [site, setSite] = useState({});
  const [programs, setPrograms] = useState(null);
  const reload = () => {
    api.get("/public/settings").then((r) => setSite(r.data)).catch(() => {});
    api.get("/public/programs").then((r) => setPrograms(r.data)).catch(() => setPrograms([]));
  };
  useEffect(() => {
    if (!sessionStorage.getItem("hr_landing")) sessionStorage.setItem("hr_landing", window.location.pathname);
    reload();
  }, []);
  return <SiteCtx.Provider value={{ site, programs: programs || [], programsLoaded: programs !== null, reload }}>{children}</SiteCtx.Provider>;
}
export const useSite = () => useContext(SiteCtx);

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

export function orgSchema(site) {
  return {
    "@context": "https://schema.org", "@type": ["EducationalOrganization", "LocalBusiness"], name: site.org_name || "Healthcare Resources",
    url: ORIGIN, logo: site.logo_url, telephone: site.phone, email: site.email, faxNumber: site.fax,
    address: { "@type": "PostalAddress", streetAddress: site.address, addressLocality: site.city, addressRegion: site.state, postalCode: site.zip, addressCountry: "US" },
    areaServed: ["Augusta, GA", "Martinez, GA", "Central Savannah River Area"],
  };
}

export function useSeo({ title, description, image, path, schema = [], noindex = false }) {
  const { site } = useSite();
  const key = JSON.stringify([title, description, image, path, schema, site?.org_name]);
  useEffect(() => {
    const t = title ? `${title} | ${site.org_name || "Healthcare Resources"}` : site.seo_default_title || "Healthcare Resources";
    const d = description || site.seo_default_description || "";
    const url = ORIGIN + (path ?? window.location.pathname);
    document.title = t;
    setMeta("name", "description", d);
    setMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    [["og:title", t], ["og:description", d], ["og:url", url], ["og:type", "website"], ["og:image", image || site.hero?.poster_url]].forEach(([k, v]) => setMeta("property", k, v));
    [["twitter:card", "summary_large_image"], ["twitter:title", t], ["twitter:description", d], ["twitter:image", image || site.hero?.poster_url]].forEach(([k, v]) => setMeta("name", k, v));
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;
    document.querySelectorAll("script[data-seo]").forEach((s) => s.remove());
    [orgSchema(site), ...schema].forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seo = "1";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export const breadcrumb = (items) => ({
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  itemListElement: items.map(([name, p], i) => ({ "@type": "ListItem", position: i + 1, name, item: ORIGIN + p })),
});
export const faqSchema = (faqs) => ({
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
});
export const sitemapUrl = `${BACKEND}/api/sitemap.xml`;
